import { CollapseProps, DialogProps as MUIDialogProps } from "@mui/material";
import { Dialog, Props as DialogProps } from "./Dialog";
import { DialogMode } from "providers/minimizedDialogs";

type MinimizableDialogProps = Omit<DialogProps, "CollapseProps"> & {
  /** Display mode: 'centered' shows full dialog centered with backdrop, 'minimizedInTray' shows collapsed in toolbar, 'expandedInTray' shows full content in toolbar (no backdrop) */
  mode?: DialogMode;
  /** Index in tray stack (for calculating bottom offset when stacking multiple dialogs in tray) */
  trayIndex?: number;
  /** Props to pass to the Collapse component that wraps the dialog content */
  CollapseProps?: CollapseProps;
};

export function MinimizableDialog(props: MinimizableDialogProps) {
  const {
    mode = "centered",
    trayIndex,
    CollapseProps: collapseProps,
    onClose,
    ...dialogProps
  } = props;

  const transitionDuration = "700ms";
  const transitionEasing = "cubic-bezier(0.4, 0, 0.2, 1)";
  const isInTray = mode === "minimizedInTray" || mode === "expandedInTray";
  const isMinimized = mode === "minimizedInTray";
  const bottomOffset = trayIndex !== undefined ? 16 + trayIndex * 80 : 16;
  const rightOffset = 16;

  return (
    <Dialog
      {...dialogProps}
      onClose={isInTray ? undefined : onClose}
      hideBackdrop={isInTray}
      disableEscapeKeyDown={isInTray}
      disableScrollLock={isInTray}
      disableEnforceFocus={isInTray}
      disableAutoFocus={isInTray}
      disableRestoreFocus={isInTray}
      slotProps={{
        ...(dialogProps as MUIDialogProps).slotProps,
        ...(isInTray && {
          root: {
            sx: {
              pointerEvents: "none",
              "& > .MuiDialog-container": {
                pointerEvents: "none",
                alignItems: "flex-end",
                justifyContent: "flex-end",
                padding: `${bottomOffset}px ${rightOffset}px`,
                transition: `padding ${transitionDuration} ${transitionEasing}, align-items ${transitionDuration} ${transitionEasing}, justify-content ${transitionDuration} ${transitionEasing}`,
              },
            },
          } as never,
        }),
      }}
      PaperProps={{
        ...(dialogProps as MUIDialogProps).PaperProps,
        sx: {
          transition: `transform ${transitionDuration} ${transitionEasing}, max-width ${transitionDuration} ${transitionEasing}, margin ${transitionDuration} ${transitionEasing}`,
          ...(isInTray
            ? {
                position: "relative",
                margin: 0,
                marginBottom:
                  trayIndex !== undefined ? `${trayIndex * 80}px` : 0,
                maxWidth: isMinimized ? 550 : 600,
                transform: isMinimized ? "scale(0.85)" : "scale(1)",
                transformOrigin: "bottom right",
                zIndex: trayIndex !== undefined ? trayIndex : 0,
                pointerEvents: "auto",
              }
            : {
                transform: "scale(1)",
                transformOrigin: "center center",
              }),
          ...(dialogProps as MUIDialogProps).PaperProps?.sx,
        },
      }}
      CollapseProps={{
        in: mode !== "minimizedInTray",
        timeout: 700,
        sx: {
          transition: `height ${transitionDuration} ${transitionEasing}, opacity ${transitionDuration} ${transitionEasing}`,
        },
        ...collapseProps,
      }}
    />
  );
}
